import { Stack, ButtonReset } from '@koob/margaret';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import styled, { useTheme } from 'styled-components';
import * as Yup from 'yup';
import { KoobButton, KoobInputTextareaField } from '@koob/ui';
import { Text, SectionCard } from 'ui';
import { RoomTag } from 'components/BookingTunnel/UIComponents';

const CardTitle = styled(Text).attrs({
  type: 'bodyLarge',
  fontWeight: 700,
  color: 'text',
})`
  &:first-letter {
    text-transform: uppercase;
  }
`;

const OtherProposalAdminBookingForm = ({ onSubmit, onClose, booking }) => {
  const theme = useTheme();
  const { t } = useTranslation('bookings');

  const initialValues = {
    hotelKeeperMessage: '',
    roomCompositions: booking?.roomCompositions?.map(({ id }) => ({
      roomCompositionId: id,
      otherProposal: false,
    })),
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        hotelKeeperMessage: Yup.string().required(t('errors:required')),
      })}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {({ isSubmitting, values, setFieldValue }) => (
        <Form>
          <Stack size="full" direction="column" paddingTop={1} gutterSize={2}>
            <Stack direction="column" size="full" gutterSize={1}>
              {booking?.roomCompositions.map((roomComposition) => {
                const isSelected = values?.roomCompositions.some(
                  ({ roomCompositionId, otherProposal }) =>
                    roomCompositionId === roomComposition.id && otherProposal,
                );
                const bedRooms = roomComposition?.bedCompositions
                  ?.map((bedCompo) => `${bedCompo.quantity} ${bedCompo.label}`)
                  ?.join(', ');

                return (
                  <SectionCard
                    key={roomComposition.id}
                    as={ButtonReset}
                    onClick={() => {
                      const draftRoomCompositions =
                        values?.roomCompositions.map(
                          ({ roomCompositionId, otherProposal }) => {
                            if (roomCompositionId === roomComposition.id) {
                              return {
                                roomCompositionId,
                                otherProposal: !otherProposal,
                              };
                            }
                            return {
                              roomCompositionId,
                              otherProposal,
                            };
                          },
                        );

                      setFieldValue('roomCompositions', draftRoomCompositions);
                    }}
                    isSelected={isSelected}
                  >
                    <Stack gutterSize={0.75} size="full">
                      <Stack marginTop={0.188}>
                        {isSelected ? (
                          <i
                            className="fa-light fa-circle-dot"
                            style={{ fontSize: 24, color: theme.primary }}
                          />
                        ) : (
                          <i
                            className="fa-light fa-circle"
                            style={{ fontSize: 24 }}
                          />
                        )}
                      </Stack>
                      <Stack direction="column" size="full" gutterSize={1}>
                        <Stack
                          size="full"
                          gutterSize={1}
                          alignX="space-between"
                          alignY="flex-end"
                        >
                          <Stack direction="column" gutterSize={0.375}>
                            <div>
                              <CardTitle>
                                {roomComposition?.hotelRoom?.displayName}
                              </CardTitle>
                            </div>
                            <Stack gutterSize={0.5} alignY="center">
                              <RoomTag
                                value={t('nGuests', {
                                  count: roomComposition?.travelers?.length,
                                })}
                                icon={
                                  <i
                                    className="fa-light fa-users"
                                    style={{ fontSize: 18 }}
                                  />
                                }
                              />
                              <RoomTag
                                value={bedRooms}
                                icon={
                                  <i
                                    className="fa-regular fa-hotel"
                                    style={{ fontSize: 18 }}
                                  />
                                }
                              />
                            </Stack>
                          </Stack>

                          <Stack direction="column" alignX="end">
                            <Stack direction="row" gutterSize={1}>
                              <Text
                                type="bodyLarge"
                                color="text"
                                fontWeight={700}
                              >
                                {t('bookingRate')}
                              </Text>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Stack>
                  </SectionCard>
                );
              })}
            </Stack>
            <KoobInputTextareaField
              name="hotelKeeperMessage"
              label={t('hotelKeeper:newProposal')}
              required
            />
            <Stack size="full" alignX="flex-end" gutterSize={1}>
              <KoobButton type="button" variant="secondary" onClick={onClose}>
                {t('misc:cancel')}
              </KoobButton>
              <KoobButton
                variant="primary"
                loading={isSubmitting}
                type="submit"
                disabled={
                  !values?.roomCompositions?.some(
                    ({ otherProposal }) => otherProposal === true,
                  )
                }
              >
                {t('hotelKeeper:submit.otherProposal')}
              </KoobButton>
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default OtherProposalAdminBookingForm;
